import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useChatStore } from '@/stores/chat-store';
import { useAiStream } from '@/hooks/use-ai-stream';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface AiChatInputProps {
  conversationId: string;
}

/** AI input with streaming state, inline error, and message restore on failure */
export function AiChatInput({ conversationId }: AiChatInputProps): React.JSX.Element {
  const [value, setValue] = useState('');
  const isAiStreaming = useChatStore((s) => s.isAiStreaming);
  const aiError = useChatStore((s) => s.aiError);
  const clearAiError = useChatStore((s) => s.clearAiError);
  const { sendMessage } = useAiStream();
  const savedMessageRef = useRef('');

  useEffect(() => {
    if (aiError) {
      setValue(savedMessageRef.current);
    }
  }, [aiError]);

  async function handleSend(): Promise<void> {
    const content = value.trim();
    if (!content || isAiStreaming || !conversationId) return;

    savedMessageRef.current = content;
    setValue('');
    clearAiError();
    await sendMessage(conversationId, content);
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-echo-border pt-3">
      <AnimatePresence>
        {aiError && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden rounded-md bg-destructive/15 px-3 py-1.5 text-xs text-red-400"
          >
            {aiError}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex gap-2">
        <Input
          className="flex-1"
          placeholder="Ask AI..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          size="icon"
          variant="ai"
          onClick={handleSend}
          disabled={isAiStreaming || !value.trim()}
          title="Ask AI"
        >
          <Sparkles className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
